import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveDoctorScheduleThunk } from "../../../redux/Thunk/Appointment/AppointmentThunks";
import CustomSelect from "../../common/CustomSelect";

const SettingsIcon = () => (
  <svg className="w-5 h-5 text-gray-800 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const DAYS = [
  { key: "MON", label: "Monday" },
  { key: "TUE", label: "Tuesday" },
  { key: "WED", label: "Wednesday" },
  { key: "THU", label: "Thursday" },
  { key: "FRI", label: "Friday" },
  { key: "SAT", label: "Saturday" },
  { key: "SUN", label: "Sunday" },
];

const DoctorSlotConfigPanel = () => {
  const dispatch = useDispatch();
  const doctorSchedule = useSelector((state) => state.appointment.doctorSchedule);

  const [slotDurationMinutes, setSlotDurationMinutes] = useState(15);
  const [defaultNewFee, setDefaultNewFee] = useState(500);
  const [defaultFollowupFee, setDefaultFollowupFee] = useState(300);
  const [weeklySchedule, setWeeklySchedule] = useState({
    MON: { active: true, shift1Start: "09:00", shift1End: "13:00", shift2Start: "17:00", shift2End: "21:00" },
    TUE: { active: true, shift1Start: "09:00", shift1End: "13:00", shift2Start: "17:00", shift2End: "21:00" },
    WED: { active: true, shift1Start: "09:00", shift1End: "13:00", shift2Start: "17:00", shift2End: "21:00" },
    THU: { active: true, shift1Start: "09:00", shift1End: "13:00", shift2Start: "17:00", shift2End: "21:00" },
    FRI: { active: true, shift1Start: "09:00", shift1End: "13:00", shift2Start: "17:00", shift2End: "21:00" },
    SAT: { active: true, shift1Start: "09:00", shift1End: "13:00", shift2Start: "17:00", shift2End: "21:00" },
    SUN: { active: false, shift1Start: "10:00", shift1End: "13:00", shift2Start: "", shift2End: "" },
  });

  useEffect(() => {
    if (doctorSchedule) {
      if (doctorSchedule.slotDurationMinutes) setSlotDurationMinutes(doctorSchedule.slotDurationMinutes);
      if (doctorSchedule.defaultNewFee) setDefaultNewFee(doctorSchedule.defaultNewFee);
      if (doctorSchedule.defaultFollowupFee) setDefaultFollowupFee(doctorSchedule.defaultFollowupFee);
      if (doctorSchedule.weeklySchedule) setWeeklySchedule(doctorSchedule.weeklySchedule);
    }
  }, [doctorSchedule]);

  const handleToggleDay = (dayKey) => {
    setWeeklySchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        active: !prev[dayKey]?.active,
      },
    }));
  };

  const handleScheduleTimeChange = (dayKey, field, value) => {
    setWeeklySchedule((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await dispatch(
      saveDoctorScheduleThunk({
        slotDurationMinutes: parseInt(slotDurationMinutes, 10),
        defaultNewFee: parseFloat(defaultNewFee),
        defaultFollowupFee: parseFloat(defaultFollowupFee),
        weeklySchedule,
      })
    );
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <SettingsIcon />
            <span>OPD Shift & Consultation Settings</span>
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Configure working shifts, appointment slot intervals & default consultation fees
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Slot Config */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Slot Interval Duration
            </label>
            <CustomSelect
              value={slotDurationMinutes}
              onChange={(val) => setSlotDurationMinutes(val)}
              options={[
                { value: 10, label: "10 Minutes" },
                { value: 15, label: "15 Minutes" },
                { value: 20, label: "20 Minutes" },
                { value: 30, label: "30 Minutes" },
              ]}
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Default New Patient Fee (₹)
            </label>
            <input
              type="number"
              value={defaultNewFee}
              onChange={(e) => setDefaultNewFee(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold bg-white outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Default Follow-up Fee (₹)
            </label>
            <input
              type="number"
              value={defaultFollowupFee}
              onChange={(e) => setDefaultFollowupFee(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold bg-white outline-none"
            />
          </div>
        </div>

        {/* Weekly Schedule Days */}
        <div>
          <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">
            Weekly OPD Shift Windows
          </h3>
          <div className="space-y-3">
            {DAYS.map((day) => {
              const dayData = weeklySchedule[day.key] || { active: false };
              return (
                <div
                  key={day.key}
                  className={`p-3.5 rounded-2xl border transition-all ${
                    dayData.active
                      ? "bg-white border-primary-200 shadow-xs"
                      : "bg-gray-50/50 border-gray-100 opacity-60"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 w-28 shrink-0">
                      <input
                        type="checkbox"
                        checked={!!dayData.active}
                        onChange={() => handleToggleDay(day.key)}
                        className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
                      />
                      <span className="text-xs font-bold text-gray-900">{day.label}</span>
                    </div>

                    {dayData.active ? (
                      <div className="flex-1 w-full flex flex-wrap items-center gap-3 text-xs">
                        {/* Shift 1 */}
                        <div className="flex-1 min-w-[280px] sm:min-w-[310px] flex items-center justify-between gap-2 bg-gray-50 px-3.5 py-2 rounded-xl border border-gray-200/70">
                          <span className="font-bold text-[11px] text-gray-700 shrink-0">Shift 1:</span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <input
                              type="time"
                              value={dayData.shift1Start || "09:00"}
                              onChange={(e) =>
                                handleScheduleTimeChange(day.key, "shift1Start", e.target.value)
                              }
                              className="w-[110px] sm:w-[118px] px-2 py-1 text-xs font-bold rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-primary-500 shadow-2xs cursor-pointer"
                            />
                            <span className="text-gray-400 font-bold">-</span>
                            <input
                              type="time"
                              value={dayData.shift1End || "13:00"}
                              onChange={(e) =>
                                handleScheduleTimeChange(day.key, "shift1End", e.target.value)
                              }
                              className="w-[110px] sm:w-[118px] px-2 py-1 text-xs font-bold rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-primary-500 shadow-2xs cursor-pointer"
                            />
                          </div>
                        </div>

                        {/* Shift 2 */}
                        <div className="flex-1 min-w-[280px] sm:min-w-[310px] flex items-center justify-between gap-2 bg-gray-50 px-3.5 py-2 rounded-xl border border-gray-200/70">
                          <span className="font-bold text-[11px] text-gray-700 shrink-0">Shift 2:</span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <input
                              type="time"
                              value={dayData.shift2Start || "17:00"}
                              onChange={(e) =>
                                handleScheduleTimeChange(day.key, "shift2Start", e.target.value)
                              }
                              className="w-[110px] sm:w-[118px] px-2 py-1 text-xs font-bold rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-primary-500 shadow-2xs cursor-pointer"
                            />
                            <span className="text-gray-400 font-bold">-</span>
                            <input
                              type="time"
                              value={dayData.shift2End || "21:00"}
                              onChange={(e) =>
                                handleScheduleTimeChange(day.key, "shift2End", e.target.value)
                              }
                              className="w-[110px] sm:w-[118px] px-2 py-1 text-xs font-bold rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-primary-500 shadow-2xs cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-[11px] font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                        Day Off
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
          <button
            type="submit"
            className="px-6 py-2.5 text-xs font-bold text-white bg-gray-900 hover:bg-black rounded-xl shadow-md transition-all hover:scale-[1.02] cursor-pointer"
          >
            Save Schedule Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default DoctorSlotConfigPanel;
